/**
 * 動画書き出しの判断(#181 / [ADR-0036](../../../../docs/adr/0036-video-export-webcodecs.md) 決定 6)。
 *
 * **符号化そのものは検査できない**([ADR-0019](../../../../docs/adr/0019-dev-foundation.md))——
 * WebCodecs は jsdom で動かない。**長さ・寸法・本数の決め方と、撮り進め方の判断
 * (撮る / 待つ / 終える / 止まったとみなす —— #354)だけをここに集めて単体テストする。**
 */

/** フレームレート。**30fps**(ADR-0036 決定 6)。 */
export const EXPORT_FPS = 30;

/**
 * 書き出しの長さの上限(秒)。**60 秒**(ADR-0036 決定 6)。
 *
 * **長い素材で待ち時間が青天井になるのを防ぐ。** 超える分は切り、切ったことを画面に出す。
 */
export const EXPORT_MAX_SECONDS = 60;

/**
 * 出力のビットレート(bps)。**1080p で 10Mbps**(ADR-0036 決定 6)。
 *
 * **書き出した動画は、このシステムに素材として戻ってきうる。**
 * 2026-08-12 に**複数動画が重かった原因は素材の約 50Mbps** と確定しており
 * ([HANDOVER.md](../../../../docs/HANDOVER.md) 3 節)、**自分が吐く動画で同じ問題を作らない。**
 */
const BITRATE_AT_1080P = 10_000_000;

/** 1080p の画素数。ビットレートを画素数に比例させる基準。 */
const PIXELS_AT_1080P = 1920 * 1080;

/**
 * **下限**(bps)。小さい盤面で極端に低いビットレートにすると、
 * **確認に使えない画質**になる。
 */
const BITRATE_FLOOR = 1_000_000;

export interface Size {
  readonly width: number;
  readonly height: number;
}

/**
 * 書き出しの長さ(秒)を決める。
 *
 * **最も長い動画素材 1 周**(ADR-0036 決定 6)—— 素材はループ再生(設計書 423)なので、
 * **1 周すれば全部の見え方が出る。**
 *
 * **上限 60 秒で切る。** 切ったかどうかは [`isTruncated`](#isTruncated) で分かる。
 *
 * **長さの取れない素材は数えない** —— `<video>` の `duration` は
 * メタデータが届くまで `NaN`、ストリームだと `Infinity` になる。
 * **混ざったまま最大値を取ると `NaN` が伝染する。**
 */
export function exportDuration(durations: readonly number[]): number {
  const usable = durations.filter((d) => Number.isFinite(d) && d > 0);
  if (usable.length === 0) return 0;
  return Math.min(Math.max(...usable), EXPORT_MAX_SECONDS);
}

/** 上限で切ったか(利用者に伝えるため)。 */
export function isTruncated(durations: readonly number[]): boolean {
  const usable = durations.filter((d) => Number.isFinite(d) && d > 0);
  if (usable.length === 0) return false;
  return Math.max(...usable) > EXPORT_MAX_SECONDS;
}

/**
 * 出力の寸法に見合うビットレート(bps)。
 *
 * **画素数に比例させる** —— 小さい絵に 10Mbps は無駄で、
 * **書き出しの時間とファイルの大きさだけが増える。**
 */
export function bitrateFor(size: Size): number {
  const pixels = size.width * size.height;
  if (!Number.isFinite(pixels) || pixels <= 0) return BITRATE_FLOOR;
  return Math.max(BITRATE_FLOOR, Math.round((pixels / PIXELS_AT_1080P) * BITRATE_AT_1080P));
}

/**
 * 進捗(0〜1)。
 *
 * **`total` が 0 でも壊れない** —— 0 除算で `NaN` が出ると、
 * 進捗の表示が「NaN%」になる。
 */
export function progressOf(done: number, total: number): number {
  if (!Number.isFinite(total) || total <= 0) return 0;
  const ratio = done / total;
  if (!Number.isFinite(ratio)) return 0;
  return Math.min(1, Math.max(0, ratio));
}

// ---------------------------------------------------------------------------
// 撮り進め方(#354)
// ---------------------------------------------------------------------------

/**
 * 基準の素材が**進まない**とみなすまでの時間(ms)(#354)。
 *
 * **自動再生を拒まれた・デコードが止まった素材では、書き出しが 0% のまま止まり続けていた。**
 * この時間を超えたら失敗として止める(「書き出せませんでした」)。
 *
 * **誤って止めない側に余裕を取る。** 0.4 倍速まで落としても素材は進み続けるので、
 * 正常なら数秒止まることもない —— ただし URL で渡された素材は途中で読み込みを待つことがある。
 */
export const STALL_LIMIT_MS = 10_000;

/**
 * 書き出しのループの 1 回分でやること(#354)。
 *
 * - `capture` —— 1 枚撮る
 * - `wait` —— 撮る時刻まで素材が進むのを待つ
 * - `done` —— 撮り終えた
 * - `stalled` —— 素材が進まない。**失敗として止める**
 */
export type CaptureAction = 'capture' | 'wait' | 'done' | 'stalled';

export interface CaptureInput {
  /** 基準の素材の位置(秒)。 */
  readonly mediaTime: number;
  /** 基準の素材が終端に着いたか(`HTMLMediaElement.ended`)。 */
  readonly ended: boolean;
  /** 書き出しの長さ(秒)。[`exportDuration`](#exportDuration) の値。 */
  readonly duration: number;
  /** 次に撮りたい時刻(秒)。 */
  readonly nextStamp: number;
  /** 基準の素材が進んでいない時間(ms)。[`watchStall`](#watchStall) の値。 */
  readonly stalledMs: number;
}

/**
 * 書き出しのループの 1 回分でやることを決める(#354)。
 *
 * ## 終端は「位置が `duration` に着いた」か `ended` で見る
 *
 * **基準の素材はループさせない**([`startCapture`](./encode.ts))。ループさせると終端で 0 に戻り、
 * **位置が `duration` に見えるのは切れ目の一瞬(画面の更新 1 回ぶん程度)だけ**になる ——
 * そこを読み逃すと**もう 1 周撮り続けていた**(#354 の実測で、毎回 1 周ぶん余計にかかった)。
 * ループさせなければ終端で位置が止まるので、**読む間隔によらず取りこぼさない。**
 *
 * ## 「終える」を「止まった」より先に見る
 *
 * **終端に着いた素材はもう進まない。** 先に止まった判定をすると、撮り終えた書き出しを
 * 失敗として捨ててしまう。
 */
export function captureAction(input: CaptureInput): CaptureAction {
  if (input.ended || input.mediaTime >= input.duration) return 'done';
  if (input.stalledMs >= STALL_LIMIT_MS) return 'stalled';
  return input.mediaTime < input.nextStamp ? 'wait' : 'capture';
}

/** 基準の素材が進んでいるかの見張り(#354)。 */
export interface StallWatch {
  /** 前回見た位置(秒)。 */
  readonly lastTime: number;
  /** 進んでいない時間(ms)。 */
  readonly stalledMs: number;
}

/** 見張りを始める。**まだ何も数えていない。** */
export function startStallWatch(mediaTime: number): StallWatch {
  return { lastTime: mediaTime, stalledMs: 0 };
}

/**
 * 見張りを 1 回進める(#354)。
 *
 * **位置が変わっていれば数え直す。** 変わっていなければ、前回からの経過時間を足す。
 *
 * ## ページが隠れていた時間は数えない
 *
 * **隠れている間は `requestAnimationFrame` が止まり、書き出しのループも止まる。**
 * 戻った最初の 1 回には**隠れていた時間がまとめて乗る**ので、そのまま足すと
 * 戻った瞬間に「止まった」と判定してしまう。`wasHidden` のときは数え直す ——
 * **戻ってから 10 秒**で判定する(同意書 Q2)。
 */
export interface HiddenMark {
  /** **前回の判断から隠れていたか。** [`watchStall`](#watchStall) の `wasHidden` に渡す。 */
  readonly wasHidden: boolean;
  /** 次の回へ持ち越す印。 */
  readonly next: boolean;
}

/**
 * 「前回から隠れていた」の印を進める(#354)。
 *
 * `marked` は `visibilitychange` で隠れたときに立てた印、`hiddenNow` はいま隠れているか。
 *
 * **持ち越す印は「いま隠れているか」にする。** 無条件に消すと、隠れたまま走った回
 * (書き出しのループの 1 回目は rAF を待たない)の後に戻ったとき、**隠れていた時間が
 * まるごと止まっていた時間に数えられ、戻った瞬間に「止まった」になる**(#354 の最終レビュー)。
 */
export function advanceHiddenMark(marked: boolean, hiddenNow: boolean): HiddenMark {
  return { wasHidden: marked || hiddenNow, next: hiddenNow };
}

export function watchStall(
  prev: StallWatch,
  mediaTime: number,
  elapsedMs: number,
  wasHidden: boolean,
): StallWatch {
  if (wasHidden || mediaTime !== prev.lastTime) return startStallWatch(mediaTime);
  // 時計が戻る・壊れた値は足さない。**誤って止めない側に倒す。**
  const step = Number.isFinite(elapsedMs) && elapsedMs > 0 ? elapsedMs : 0;
  return { lastTime: mediaTime, stalledMs: prev.stalledMs + step };
}
